import { useFetcher, useParams } from 'react-router';
import { useEffect } from 'react';
import { protectedResourceLoader } from '~/shell/utils/authUtil.server';
import { fetchTemplateLookups } from '~/features/templates/templates.server';
import { PgArea, TgArea } from '~/common/types/area';
import { promiseHash } from 'remix-utils/promise';
import { getLatestTemplateIdFromContext } from '~/shell/globalContext.ts';
import { WebTemplateTypes } from '../../types/webTemplateType';
import type { Language } from '~/common/types/language';
import { fallbackLng } from '~/i18nConfig';

export const loader = protectedResourceLoader(async ({ area, api, url }) => {
  try {
    const templateType = area === TgArea ? WebTemplateTypes.BGH : WebTemplateTypes.AGH;
    const mainArticleIdParam = url.searchParams.get('mainArticleId');
    const mainArticleId = mainArticleIdParam ? Number.parseInt(mainArticleIdParam, 10) : undefined;

    const results = await promiseHash({
      response: fetchTemplateLookups(api, templateType, mainArticleId),
      latestTemplateId: getLatestTemplateIdFromContext() //getLastestTemplateId(request)
    });

    return {
      latestTemplateId: results.latestTemplateId,
      templates: results.response?.templates,
      ok: true
    };
  } catch {
    return { ok: false };
  }
});

export function useAllTemplates(
  isPgArea: boolean,
  articles: { mainArticleId: number; isExternalArticle: boolean; quantity: number }[]
) {
  const { load, data, state } = useFetcher<typeof loader>();
  const { lang: langParam } = useParams() as { lang?: Language };
  const lang = langParam ?? fallbackLng;

  let url = `/${lang}/${isPgArea ? PgArea : TgArea}/resources/templates`;

  if (articles.length === 1) {
    url = `${url}?mainArticleId=${articles[0].mainArticleId}`;
  }

  useEffect(() => {
    load(url);
  }, [lang, load, url]);

  return {
    templates: data?.templates,
    isLoading: state !== 'idle',
    latestTemplateId: data?.latestTemplateId,
    ok: data?.ok
  };
}
